import { computed, DOCUMENT, inject, Injectable, signal } from '@angular/core';
import { forkJoin } from 'rxjs';
import { first, switchMap, tap } from 'rxjs/operators';
import { CurrencyService } from './currency.service';
import { CountryService } from './country.service';
import { BrandConfigService } from './brand-config/brand-config.service';
import { TranslationService } from '../shared/translation/translation.service';
import { AbTestNewService } from '../ab-test/ab-test.service';
import { PlatformService } from 'ngx-unificator/services';
import { IS_BOT } from '../../../express.tokens';

@Injectable({
  providedIn: 'root',
})
export class DataPreloaderService {
  private _translate = inject(TranslationService);
  private _ab = inject(AbTestNewService);
  private _currency = inject(CurrencyService);
  private _country = inject(CountryService);
  private _brandConfig = inject(BrandConfigService);
  private _document = inject(DOCUMENT);
  private _platform = inject(PlatformService);
  private _isBot = inject(IS_BOT, { optional: true });



  /**
   * An array of critical data that needs to be preloaded before the application can fully function.
   * This includes loading countries, translations, and currencies.
   */
  private _criticalDataToPreload: any[] = [
    this._currency.loadCurrencies(),
    this._country.loadCountries(),
    this._translate.loadTranslates(),
  ];


  /**
   * An array of third-party data that needs to be preloaded before the application can fully function.
   * This may include data from external services or APIs that the application depends on.
   */
  private _thirdPartyDataToPreload: any[] = [
  ];

  /**
   * A computed property that indicates whether all critical data has been loaded.
   * This includes the loading of countries, translations, and currencies.
   * @returns {boolean} `true` if all critical data has been loaded, `false` otherwise.
   */
  public criticalDataLoaded = computed(() => this._currency.loaded() && this._country.loaded() && this._translate.loaded());
  public initialLoadDone = signal(false); // for prevent showing loader on translation load

  /**
   * The `preloadCriticalData` function preloads critical data by resolving language, fetching brand
   * configuration, and preloading necessary data.
   */
  public preloadCriticalData() {
    this._brandConfig.brandConfigResolved$.pipe(
      switchMap(() => forkJoin([...this._criticalDataToPreload])),
      tap(() => this.initialLoadDone.set(true)),
      first()
    ).subscribe(() => {
      this._destroyLoader();
    });
  }

  /**
   * The function `_destroyLoader` removes the browser loader element from the document if it exists.
   */
  private _destroyLoader() {
    const loader = this._document?.getElementById('browser-loader');
    if (this._isBot && !this._platform.isBrowser) {
      loader?.remove();
    }
    if (this._platform.isBrowser && loader) {
      loader?.remove();
    }
  }

}
